import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import Badge from "@mui/material/Badge";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Popover from "@mui/material/Popover";
import Tooltip from "@mui/material/Tooltip";
import {useId} from "react";
import {useTranslation} from "react-i18next";
import {useServerTableContext} from "../ServerTableContext";
import {
    useServerTableToolbarContext,
    useServerTableToolbarItemContext
} from "../ServerTableToolbarContext";
import {defineServerTableToolbarItem} from "../ServerTableToolbarItem";
import {serverTableControlIconSx, serverTableControlSx} from "../constants";
import type {ServerTableFilterProps} from "../types";
import FilterPanel from "./FilterPanel";

const ServerTableFilterComponent = ({options, disabled}: ServerTableFilterProps) => {
    const {t} = useTranslation();
    const {filterRules, setFilterRules} = useServerTableContext();
    const popoverId = useId();
    const {activePanel, openPanel, closePanel} = useServerTableToolbarContext();
    const toolbarItem = useServerTableToolbarItemContext();
    const isOpen = activePanel?.type === 'filter';
    const openFilter = (anchor: HTMLElement) => {
        toolbarItem.closeMenu();
        openPanel('filter', toolbarItem.menuAnchor ?? anchor);
    };

    return (
        <>
            {toolbarItem.presentation === 'menu' ? (
                <MenuItem
                    disabled={disabled}
                    onClick={event => openFilter(event.currentTarget)}
                >
                    <Badge color="primary" badgeContent={filterRules.length} sx={{mr: 1}}>
                        <FilterAltOutlinedIcon fontSize="small" sx={{color: 'primary.main'}}/>
                    </Badge>
                    {t('Filter')}
                </MenuItem>
            ) : (
                <Tooltip title={t('Filter')}>
                    {/* The span keeps the tooltip working while the button is disabled. */}
                    <span>
                        <IconButton
                            size="small"
                            color="primary"
                            data-dashboard-control=""
                            data-active={isOpen || undefined}
                            aria-label={t('Filter')}
                            aria-haspopup="dialog"
                            aria-expanded={isOpen}
                            aria-controls={isOpen ? popoverId : undefined}
                            disabled={disabled}
                            onClick={event => openFilter(event.currentTarget)}
                            sx={serverTableControlSx}
                        >
                            <Badge color="primary" badgeContent={filterRules.length}>
                                <FilterAltOutlinedIcon sx={serverTableControlIconSx}/>
                            </Badge>
                        </IconButton>
                    </span>
                </Tooltip>
            )}

            <Popover
                open={isOpen}
                anchorEl={isOpen ? activePanel.anchor : null}
                onClose={closePanel}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                transformOrigin={{vertical: 'top', horizontal: 'right'}}
                PaperProps={{id: popoverId, role: 'dialog', 'aria-label': t('Filter')}}
            >
                <FilterPanel
                    filterOptions={options}
                    rules={filterRules}
                    onChange={setFilterRules}
                    onClose={closePanel}
                />
            </Popover>
        </>
    );
};

export const ServerTableFilter = defineServerTableToolbarItem(ServerTableFilterComponent);
